/* Cart status announcements — Patch for Accessibility — Start
   Finding plate-E22 (WCAG 4.1.3 Status Messages): cart updates were not announced
   to screen readers. Prestige exposes a global polite live region
   (#status-announcement, present and empty on load) but the cart flow wasn't
   writing to it for adds, removals, or quantity changes. This listens for the
   theme's cart:change event (dispatched for every cart update across product
   cards, PDP, quick-buy, and the cart drawer), diffs the cart against the
   previous state, and announces what changed:
     • new line item                → "<Product> added to your cart"
     • quantity increased/decreased → "<Product> quantity updated to N"
     • line item removed            → "<Product> removed from your cart"
   Increases from re-adding are reported as a quantity update, so nothing is
   announced twice. */
(() => {
  const REGION_ID = 'status-announcement';
  let snapshot = null; // Map<lineKey, { title, quantity }>

  function announce(message) {
    const drawer = document.getElementById('cart-drawer');

    const region =
      (drawer?.open &&
        drawer.shadowRoot?.querySelector('[data-cart-status]')) ||
      document.getElementById(REGION_ID);
    if (!region || !message) return;
    region.textContent = '';
    // clear-then-set so the polite region reliably re-announces
    setTimeout(() => { region.textContent = message; }, 100);
  }

  function toMap(cart) {
    const map = new Map();
    ((cart && cart.items) || []).forEach((item) => {
      map.set(item.key, {
        title: item.product_title || item.title,
        quantity: item.quantity
      });
    });
    return map;
  }

  function diff(prev, next) {
    const messages = [];

    // removals and quantity decreases
    prev.forEach((was, key) => {
      const now = next.get(key);
      if (!now) {
        messages.push(`${was.title} removed from your cart`);
      } else if (now.quantity < was.quantity) {
        messages.push(`${was.title} quantity updated to ${now.quantity}`);
      }
    });

    // new items and quantity increases
    next.forEach((now, key) => {
      const was = prev.get(key);
      if (!was) {
        messages.push(`${now.title} added to your cart`);
      } else if (now.quantity > was.quantity) {
        messages.push(`${now.title} quantity updated to ${now.quantity}`);
      }
    });

    return messages;
  }

  async function getCart(event) {
    const fromEvent = event && event.detail && event.detail.cart;
    if (fromEvent && fromEvent.items) return fromEvent;
    try { return await fetch((window.Shopify?.routes?.root || '/') + 'cart.js').then((r) => r.json()); }
    catch (e) { return null; }
  }

  // seed the baseline on page load
  fetch((window.Shopify?.routes?.root || '/') + 'cart.js')
    .then((r) => r.json())
    .then((cart) => { snapshot = toMap(cart); })
    .catch(() => {});

  document.addEventListener('cart:change', async (event) => {
    const cart = await getCart(event);
    if (!cart) return;

    const next = toMap(cart);

    // if a change fires before the baseline is ready, record it silently
    if (snapshot === null) { snapshot = next; return; }

    const messages = diff(snapshot, next);
    snapshot = next;
    if (messages.length) announce(messages.join('. '));
  }, true); // capture phase — matches how the theme dispatches the event
})();
/* Cart status announcements — Patch for Accessibility — End */

/* Okendo presence gate — Patch for Performance (2026-08-22) — Start
   The two Okendo patches below each installed a permanent MutationObserver on
   document.documentElement at page load, on EVERY page — including the many
   pages where Okendo never renders (policy, cart, search, blog). Those observers
   then woke up for every DOM change on the page, forever, waiting for nodes that
   would never appear.

   This helper defers starting an observer until Okendo is actually present:
     • Okendo already in the DOM  → start immediately.
     • Not yet (it loads async)   → watch briefly for it to appear, then start
                                    and stop watching.
     • Never appears              → give up 8s after load and disconnect, so no
                                    observer is left running on non-Okendo pages.
   The accessibility fixes themselves are unchanged — this only gates WHEN their
   observers begin watching. */
window.__plateWhenOkendoPresent = function (startObserving) {
  var OKENDO_SELECTOR = '[data-oke-container], [class*="oke-"]';

  if (document.querySelector(OKENDO_SELECTOR)) {
    startObserving();
    return;
  }

  var settled = false;

  var waiter = new MutationObserver(function () {
    if (settled) return;
    if (document.querySelector(OKENDO_SELECTOR)) {
      settled = true;
      waiter.disconnect();
      startObserving();
    }
  });

  waiter.observe(document.documentElement, { childList: true, subtree: true });

  // Safety net: stop waiting on pages where Okendo never loads.
  window.addEventListener('load', function () {
    setTimeout(function () {
      if (settled) return;
      settled = true;
      waiter.disconnect();
    }, 8000);
  });
};
/* Okendo presence gate — Patch for Performance — End */


/* Okendo `.oke-w-writeReview` - Patch for Accessibility - Start */
(() => {
  const selector = 'a.oke-w-writeReview';

  function fixOkendoWriteReviewLink(root = document) {
    const links = root.matches?.(selector)
      ? [root]
      : Array.from(root.querySelectorAll?.(selector) || []);

    links.forEach((link) => {
      /*
       * Clone the link and remove Okendo's screen-reader-only text
       * so we can retrieve the actual visible button label.
       */
      const clone = link.cloneNode(true);

      clone
        .querySelectorAll('.oke-a11yText, [aria-hidden="true"]')
        .forEach((element) => element.remove());

      const visibleLabel =
        clone.textContent.replace(/\s+/g, ' ').trim() || 'Write a Review';

      const accessibleLabel = `${visibleLabel} (opens in a new window)`;

      if (link.getAttribute('aria-label') !== accessibleLabel) {
        link.setAttribute('aria-label', accessibleLabel);
      }
    });
  }

  fixOkendoWriteReviewLink();

  /*
   * Okendo loads asynchronously, so watch for the widget being added
   * or another script changing its aria-label.
   */
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        mutation.target.matches?.(selector)
      ) {
        fixOkendoWriteReviewLink(mutation.target);
      }

      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          fixOkendoWriteReviewLink(node);
        }
      });
    });
  });

  /* PERF (2026-08-22): only start watching once Okendo is actually on the page,
     so this observer no longer runs on every non-Okendo page. */
  window.__plateWhenOkendoPresent(() => {
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label']
    });
  });
})();
/* Okendo `.oke-w-writeReview` - Patch for Accessibility - End */

/* Okendo media carousel Prev/Next — Patch for Accessibility — Start
   Finding plate-E95 (WCAG 2.1.1 / 2.1.3 Keyboard): Okendo renders the
   customer-media carousel's "Previous/Next Slide" buttons with tabindex="-1",
   so keyboard users can't reach or operate them. They are native <button>s
   (Enter/Space already trigger the click handler), so the fix is to remove the
   negative tabindex and expose an accessible name from the title. Okendo loads
   and re-renders async, so an observer re-applies the fix. */
(() => {
  const selector = '.oke-mediaCarousel-prev, .oke-mediaCarousel-next';

  function fixOkendoCarouselButtons(root = document) {
    const buttons = root.matches?.(selector)
      ? [root]
      : Array.from(root.querySelectorAll?.(selector) || []);

    buttons.forEach((button) => {
      /* Put the button back in the tab order */
      if (button.getAttribute('tabindex') === '-1') {
        button.removeAttribute('tabindex');
      }

      /* Give the button an accessible name if it has none */
      if (
        !button.getAttribute('aria-label') &&
        !button.getAttribute('aria-labelledby') &&
        !button.textContent.trim()
      ) {
        const label = button.getAttribute('title');
        if (label) button.setAttribute('aria-label', label);
      }
    });
  }

  fixOkendoCarouselButtons();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.target.matches?.(selector)) {
        fixOkendoCarouselButtons(mutation.target);
      }
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) fixOkendoCarouselButtons(node);
      });
    });
  });

  /* PERF (2026-08-22): only start watching once Okendo is actually on the page,
     so this observer no longer runs on every non-Okendo page. */
  window.__plateWhenOkendoPresent(() => {
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex']
    });
  });
})();
/* Okendo media carousel Prev/Next — Patch for Accessibility — End */

/* Okendo reviews "Show more" — Patch for Accessibility — Start
   Finding plate-E102 (WCAG 2.4.3 Focus Order): activating the reviews-list
   "Show more" control appends more reviews but leaves keyboard focus on the
   button, so keyboard and screen reader users aren't taken to the newly
   revealed reviews. This moves focus to the first newly added review once it
   renders. Okendo is a third-party app that stops event propagation and loads
   reviews asynchronously, so we listen in the capture phase and watch the
   reviews container for the new items. */
(() => {
  const BUTTON = '.oke-showMore-button';
  const ROOT = '.oke-w-reviews';
  const ITEM = '.oke-w-reviews-list-item';

  document.addEventListener('click', (event) => {
    if (!event.target.closest?.(BUTTON)) return;

    const root = document.querySelector(ROOT);
    if (!root) return;

    const before = root.querySelectorAll(ITEM).length;

    const focusFirstNew = () => {
      const items = root.querySelectorAll(ITEM);
      if (items.length <= before) return false;
      const firstNew = items[before];
      firstNew.setAttribute('tabindex', '-1'); // allow programmatic focus
      firstNew.focus();
      return true;
    };

    /* Reviews render asynchronously — focus the first new one when it appears */
    const observer = new MutationObserver(() => {
      if (focusFirstNew()) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });

    /* Safety net: stop watching if nothing loads */
    setTimeout(() => observer.disconnect(), 6000);
  }, true); // capture phase — Okendo stops propagation, so bubble won't fire
})();
/* Okendo reviews "Show more" — Patch for Accessibility — End */

/* Cookie Banner  -Patch for Accessibility - Start
Added a visually hidden “Cookie preferences” heading with the ID expected by Shopify’s native privacy banner when no visible banner title is configured.
This prevents the cookie dialog from referencing a missing ID while preserving the compact, title-free visual design.
*/
(() => {
  const fixShopifyPrivacyBannerTitle = () => {
    const banner = document.getElementById('shopify-pc__banner');

    if (!banner) {
      return false;
    }

    const titleId = 'shopify-pc__banner__body-title';

    if (!document.getElementById(titleId)) {
      const title = document.createElement('span');

      title.id = titleId;
      title.className = 'sr-only';
      title.textContent = 'Cookie preferences';

      const bannerBody =
        banner.querySelector('.shopify-pc__banner__body') || banner;

      bannerBody.prepend(title);
    }

    return true;
  };

  if (fixShopifyPrivacyBannerTitle()) {
    return;
  }

  const observer = new MutationObserver(() => {
    if (fixShopifyPrivacyBannerTitle()) {
      observer.disconnect();
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
/* Cookie Banner  -Patch for Accessibility - End */

/* Contact form inline error identification — Patch for Accessibility — Start
   Finding plate-E17 (WCAG 3.3.1 Error Identification): accessible inline validation
   for the contact form — each error names its field, is associated via
   aria-describedby + aria-invalid, and persists until the field is valid.
   Notes:
   • Native type="email" validation is lenient (it accepts "a@b" with no TLD), so we
     add a stricter format check for the email field.
   • The Meta Pixel (fbevents.js) re-submits detected "lead" forms via a programmatic
     form.submit() that bypasses preventDefault, so every submission is routed through
     one validity gate. */
(() => {
  const emailField = document.querySelector('form input[name="contact[email]"]');
  const form = emailField && emailField.closest('form');
  if (!form) return;

  form.setAttribute('novalidate', '');

  // Stricter than native type="email": require a dot'd domain (a TLD).
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const fieldName = (field) => {
    const label = field.labels && field.labels[0];
    return (label ? label.textContent : field.getAttribute('name') || 'This field')
      .replace(/\s+/g, ' ').trim();
  };

  // Combines native validity with our stricter email format check.
  const isValid = (field) => {
    if (!field.checkValidity()) return false;
    if (field.type === 'email' && field.value.trim() && !EMAIL_RE.test(field.value.trim())) return false;
    return true;
  };

  const messageFor = (field) => {
    const v = field.validity;
    const name = fieldName(field);
    if (v.valueMissing) return `${name} is required.`;
    if (field.type === 'email' && field.value.trim() && (v.typeMismatch || !EMAIL_RE.test(field.value.trim())))
      return `Please enter a valid email address in ${name}.`;
    if (v.tooShort) return `${name} is too short.`;
    if (v.patternMismatch) return `${name} is not in the expected format.`;
    return field.validationMessage ? `${name}: ${field.validationMessage}` : `${name} is invalid.`;
  };

  const errorId = (field) => `${field.id}-error`;

  function showError(field) {
    let error = document.getElementById(errorId(field));
    if (!error) {
      error = document.createElement('span');
      error.id = errorId(field);
      error.className = 'form-control__error';
      (field.closest('.form-control') || field.parentElement).appendChild(error);
    }
    error.textContent = messageFor(field);
    field.setAttribute('aria-invalid', 'true');
    const ids = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    if (!ids.includes(errorId(field))) ids.push(errorId(field));
    field.setAttribute('aria-describedby', ids.join(' '));
  }

  function clearError(field) {
    const error = document.getElementById(errorId(field));
    if (error) error.remove();
    field.removeAttribute('aria-invalid');
    const ids = (field.getAttribute('aria-describedby') || '')
      .split(/\s+/).filter((id) => id && id !== errorId(field));
    if (ids.length) field.setAttribute('aria-describedby', ids.join(' '));
    else field.removeAttribute('aria-describedby');
  }

  const validateField = (field) =>
    isValid(field) ? (clearError(field), true) : (showError(field), false); // uses stricter isValid()

  const fields = () => Array.from(form.elements)
    .filter((el) => el.willValidate && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

  // Validate every field; focus the first invalid one. Returns true when valid.
  function fieldsValid() {
    let firstInvalid = null;
    fields().forEach((field) => { if (!validateField(field) && !firstInvalid) firstInvalid = field; });
    if (firstInvalid) { firstInvalid.focus(); return false; }
    return true;
  }

  // Gate the submit for accessibility WITHOUT interfering with Shopify's spam-
  // protection captcha. Shopify shows the captcha challenge and, once the visitor
  // solves it, RE-SUBMITS the form itself with the token attached. We must not
  // touch that re-submission or the challenge loops forever. So: validate in the
  // capture phase and act ONLY when a field is invalid (block + show errors). When
  // everything is valid we do nothing and let the native event proceed to Shopify's
  // captcha exactly as on stock Prestige.
  //
  // We deliberately do NOT override form.submit(). A previous version overrode it
  // (to route the Meta Pixel's programmatic submit through validation); that
  // override intercepted the captcha's post-solve re-submit and re-fired the submit
  // event, re-triggering the challenge — an unpassable captcha loop for every
  // visitor. Correctness of the form for real visitors outweighs gating the pixel's
  // edge-case auto-submit, so the override is removed.
  form.addEventListener('submit', (event) => {
    if (!fieldsValid()) {
      event.preventDefault();
      event.stopImmediatePropagation(); // don't let Shopify submit an invalid form
    }
    // valid → do nothing; Shopify's captcha and native submission handle the rest
  }, true);

  const revalidate = (event) => {
    const field = event.target;
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(field.tagName) && field.getAttribute('aria-invalid') === 'true') {
      validateField(field);
    }
  };
  form.addEventListener('input', revalidate);
  form.addEventListener('change', revalidate);
})();
/* Contact form inline error identification — Patch for Accessibility — End */

/* E24 / E87 — Mega-menu column headers → headings (not links). */
(function () {
  function enhanceMegaMenuHeadings() {
    document.querySelectorAll('.mega-menu__linklist > li > a.strong').forEach(function (el) {
      el.setAttribute('role', 'heading');
      el.setAttribute('aria-level', '2');
      el.removeAttribute('href');   // stop it being a focusable do-nothing link
    });
  }
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', enhanceMegaMenuHeadings)
    : enhanceMegaMenuHeadings();
})();

/* =========================================================
   CALM SEARCH REDIRECT
   Redirects exact storefront searches for "calm" or
   "calm serum" to the provider-exclusive CALM Serum page.

   Broader searches such as "calm skin" or "calming serum"
   continue to use normal Shopify search behavior.

   Added: October 2, 2026
   ========================================================= */
(() => {
  const CALM_URL = '/pages/provider-exclusive-calm-serum';
  const CALM_QUERIES = ['calm', 'calm serum'];

  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || '';

    if (!action.includes('/search')) return;

    const searchInput = form.querySelector('input[name="q"]');
    if (!searchInput) return;

    const query = searchInput.value.trim().toLowerCase();

    if (!CALM_QUERIES.includes(query)) return;

    event.preventDefault();
    window.location.assign(CALM_URL);
  }, true);
})();
/* ================= END CALM SEARCH REDIRECT ================= */